/**
 * Annotation Overlay Component
 *
 * Renders signature fields, text fields, dates, and initials as DOM overlays
 * on top of PDF canvas. Uses absolute positioning in PDF coordinate space.
 */

import React, { useCallback } from 'react';
import { AnnotationData, ViewerMode } from './types';
import styles from './AnnotationOverlay.module.scss';

export interface AnnotationOverlayProps {
  pageNumber: number;
  pageWidth: number;
  pageHeight: number;
  annotations: AnnotationData[];
  mode: ViewerMode;
  onAnnotationsChange: (annotations: AnnotationData[]) => void;
  onFieldClick: (field: AnnotationData) => void;
  zoom: number;
}

export const AnnotationOverlay: React.FC<AnnotationOverlayProps> = ({
  pageNumber,
  pageWidth,
  pageHeight,
  annotations,
  mode,
  onAnnotationsChange,
  onFieldClick,
  zoom,
}) => {
  /**
   * Convert PDF coordinates (bottom-left origin) to screen coordinates (top-left origin)
   */
  const pdfToScreen = useCallback((pdfY: number): number => {
    return pageHeight - pdfY;
  }, [pageHeight]);

  /**
   * Handle field click
   */
  const handleFieldClick = useCallback((field: AnnotationData) => {
    if (mode === 'signing' || mode === 'read-only') {
      onFieldClick(field);
    }
  }, [mode, onFieldClick]);

  /**
   * Render signature or initials field
   */
  const renderSignatureField = (annotation: AnnotationData) => {
    const { rect } = annotation;
    const width = rect.x2 - rect.x1;
    const height = rect.y2 - rect.y1;
    const top = pdfToScreen(rect.y2); // Top edge in screen coords
    const left = rect.x1;

    const fieldStyle: React.CSSProperties = {
      position: 'absolute',
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
      cursor: mode === 'signing' ? 'pointer' : 'default',
    };

    const hasSignature = !!annotation.imageData;

    return (
      <div
        key={annotation.id}
        className={`${styles.signatureField} ${hasSignature ? styles.completed : styles.pending}`}
        style={fieldStyle}
        onClick={() => handleFieldClick(annotation)}
        data-field-id={annotation.id}
        data-field-type={annotation.type}
      >
        {annotation.imageData ? (
          // Show signature image
          <img
            src={annotation.imageData}
            alt={annotation.type === 'signature' ? 'Signature' : 'Initials'}
            className={styles.signatureImage}
          />
        ) : (
          // Show placeholder
          <div className={styles.signaturePlaceholder}>
            <span className={styles.placeholderText}>
              {annotation.type === 'signature' ? 'Sign Here' : 'Initial Here'}
            </span>
          </div>
        )}
      </div>
    );
  };

  /**
   * Render text field
   */
  const renderTextField = (annotation: AnnotationData) => {
    const { rect } = annotation;
    const width = rect.x2 - rect.x1;
    const height = rect.y2 - rect.y1;
    const top = pdfToScreen(rect.y2); // Top edge in screen coords
    const left = rect.x1;

    const fieldStyle: React.CSSProperties = {
      position: 'absolute',
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
    };

    const isEditable = mode === 'signing';
    const hasValue = !!annotation.value;

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const updatedAnnotations = annotations.map(a =>
        a.id === annotation.id ? { ...a, value: e.target.value } : a
      );
      onAnnotationsChange(updatedAnnotations);
    };

    return (
      <div
        key={annotation.id}
        className={`${styles.textField} ${hasValue ? styles.completed : styles.pending}`}
        style={fieldStyle}
        data-field-id={annotation.id}
      >
        <textarea
          className={styles.textInput}
          value={annotation.value || ''}
          onChange={handleChange}
          readOnly={!isEditable}
          placeholder={isEditable ? 'Type here' : ''}
          style={{
            fontSize: annotation.fontSize ? `${annotation.fontSize}px` : '11px',
          }}
        />
      </div>
    );
  };

  /**
   * Render date field
   */
  const renderDateField = (annotation: AnnotationData) => {
    const { rect } = annotation;
    const width = rect.x2 - rect.x1;
    const height = rect.y2 - rect.y1;
    const top = pdfToScreen(rect.y2); // Top edge in screen coords
    const left = rect.x1;

    const fieldStyle: React.CSSProperties = {
      position: 'absolute',
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
    };

    // Auto-fill with today's date if not set
    const dateValue = annotation.value || new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

    return (
      <div
        key={annotation.id}
        className={`${styles.dateField} ${styles.completed}`}
        style={fieldStyle}
        data-field-id={annotation.id}
      >
        <div className={styles.dateValue}>{dateValue}</div>
      </div>
    );
  };

  /**
   * Render appropriate field based on type
   */
  const renderAnnotation = (annotation: AnnotationData) => {
    switch (annotation.type) {
      case 'signature':
      case 'initials':
        return renderSignatureField(annotation);
      case 'text':
        return renderTextField(annotation);
      case 'date':
        return renderDateField(annotation);
      default:
        return null;
    }
  };

  return (
    <div className={styles.annotationOverlay}>
      {annotations.map(renderAnnotation)}
    </div>
  );
};
